import { useCallback } from 'react';
import { create } from 'zustand';
import { useAuthStore } from '../../../store/auth';
import { clearSnapshot, readSnapshot, writeSnapshot } from './snapshot';
import type { FlowTrailEvent } from '../../../api/chat';
import type {
  ClarifyState,
  CommitmentInfo,
  EmotionInfo,
  FlowNextStepItem,
  IntentInfo,
  NextActionItem,
  QueryCardItem,
  RiskInfo,
  SceneCardItem,
  ScriptTipItem,
  SuggestionItem,
} from '../assist/types';

/**
 * 坐席工作台语境（context）store——规划见 docs/guangdian/48-坐席工作台会话上下文与状态规划.md。
 *
 * 解决的问题：工作台此前把「当前通话 uuid / 当前会话」与辅助、质检、流程状态都放在组件内，
 * 页内 tab 是单实例条件渲染（ChatPanel），切换即卸载整棵子树；菜单往返更是整页卸载——
 * 坐席一旦切走再回来，本通电话的辅助与质检内容全部消失（转写/聊天记录因在页面级或服务端才幸存）。
 *
 * 设计口径：
 * - **单一真源**：当前通话 uuid / 当前会话 id 只在本 store 保存，组件不再各持一份；
 * - **按 ref 分桶**：语境数据以 `call:<uuid>` / `chat:<sid>` 为键分桶，跨视图切换保留；
 * - **只允许两条写路径**：WS 事件回调（增量）与回填（幂等合并）；组件不得直接改写他人桶；
 * - **释放**：挂断/关闭后保留 10 分钟供回看，随后释放；总桶数上限 8（LRU 淘汰）。
 *
 * 注：桶是进程内内存态，整页刷新（F5）会重建本模块——故按 ref 落一份 sessionStorage 快照
 * （`context/snapshot.ts`），应用启动时回填，让「刷新后还能看到的」与切 tab / 菜单往返口径一致。
 * **快照只留「刷新不该丢、且服务端重建不出来」的那一点状态**（见 savePersisted）：
 * 在线会话指针 chatId + 该会话最新一条意图/情绪。**语音桶整桶不落盘**：软电话模式下浏览器刷新
 * 会销毁 WebRTC、这一通已经断了，落盘内容只会让工具条显示一个已失效的「实时状态」；
 * 外部话机模式刷新虽不断话，但实时意图/情绪是「当下判断」，同样宁缺勿旧（质检有回填端点兜住）。
 * 通话身份由 `/assist/active-call` 定位、「说到哪句」由 assist-forward-cursor 记。
 * 质检告警不落盘：monitor 的 quality-alerts 回填接口即可重建（双源还容易重复计数）。
 * 当前通话 uuid 不入快照：通话是否仍在以服务端 `/assist/active-call` 为真源。
 */

/** 语境类型：语音通话 / 在线会话 */
export type CtxKind = 'call' | 'chat';

/** 语境定位参数：call 用显式通话 uuid（空串=本卡无语境；省略=读 store 当前通话 uuid），chat 用会话 id */
export interface CtxSpec {
  kind: CtxKind;
  id?: number | string | null;
}

/** 实时质检告警条目（原 QualityPanel 内部类型，上移到此单源，双通道共用） */
export interface QualityAlertItem {
  key: string;
  /** 落库行 id（回填与实时事件同源去重用；落库失败/旧版载荷为空） */
  hitId: number;
  rule: string;
  type: string;
  dimension: string;
  level: string;
  deduct: number;
  reason: string;
  /** 投诉二分类（语音通道有值）：none|first_complaint|formal_complaint */
  complaintType: string;
  /** 投诉升级概率 0~1（语音通道有值；在线通道暂不判投诉，恒 0） */
  escalationProb: number;
  /** 告警类型：rule=规则命中 / emotion=情绪预警 / emotion_trend=情绪升级趋势 / rule_low_score=服务结束后按评分补判的规则低分 */
  alertType: string;
  awarded?: number | null; // 低分告警（rule_low_score）触发规则的得分 0–5；其余告警为空
  ts: number;
}

/** 一句对话（双方）：转给子页时只用 speaker + text + ts */
export interface DialogueTurn {
  speaker: 'customer' | 'agent';
  text: string;
  ts: number;
}

/** 实时辅助（意图/情绪/风险 + 建议/话术/数据卡/场景卡/承诺度/流程建议）语境切片 */
export interface AssistSlice {
  suggestions: SuggestionItem[];
  expandChunks: Record<number, boolean>;
  intent: IntentInfo | null;
  intentAction: { type: string; code?: string; params?: Record<string, any>; script?: string } | null;
  emotion: EmotionInfo | null;
  /** 情绪采样序列（趋势判定用；含强度；ts=事件时间戳，用于去重） */
  emotionHistory: { emotion: string; level: string; ts?: number }[];
  risk: RiskInfo | null;
  /**
   * 双方对话缓冲（按到达顺序，最多 60 句）：语音=ASR 双方终稿、在线=访客与坐席消息。
   * 智能辅助卡只把它按序转给 WeKnora 子页（`bcc:utterance`）；**何时提问、问什么由子页决定**，
   * 宿主不攒批、不归纳、不判断该不该问。
   */
  dialogueTurns: DialogueTurn[];
  nextActions: NextActionItem[];
  flowNext: FlowNextStepItem[];
  steps: string[];
  queryCards: QueryCardItem[];
  sceneCards: SceneCardItem[];
  commitment: CommitmentInfo | null;
  scriptTips: ScriptTipItem[];
  expandAllSuggest: boolean;
  /** 实时辅助分节折叠：处理建议 / 话术库 / 知识库（按语境桶持久化，切 tab/菜单往返保留） */
  collapsedSections: { advice: boolean; script: boolean; kb: boolean };
  /** 在线会话流程踪迹（chat_flow_trail） */
  chatTrail: FlowTrailEvent[];
  chatTrailFlow: string;
  /** 澄清重搜结果（按建议卡 seq 索引） */
  clarifyResults: Record<number, ClarifyState>;
  /** 实时识别降级态（assist_degraded）：熔断期间不再产出意图/情绪/风险 */
  degrade: { reason?: string; untilMs?: number } | null;
}

/** 实时质检语境切片（告警列表 + 折叠计数 + 本语境累计） */
export interface QualitySlice {
  alerts: QualityAlertItem[];
  /** 超出展示上限被略过的条数（提示「早前 N 条」） */
  folded: number;
  /** 本语境累计命中条数（列表截断不影响该计数） */
  hits: number;
  /** 本语境累计扣分 */
  deduct: number;
}

/** 语境桶：一个 ref（一通电话 / 一个在线会话）的全部语境状态 */
export interface Bucket {
  key: string;
  kind: CtxKind;
  ref: string;
  createdAt: number;
  updatedAt: number;
  /** 通话挂断 / 会话关闭：保留供回看，超时释放 */
  ended?: boolean;
  endedAt?: number;
  assist: AssistSlice;
  quality: QualitySlice;
}

type SliceName = 'assist' | 'quality';
interface SliceMap {
  assist: AssistSlice;
  quality: QualitySlice;
}

/** 模块级常量初值：selector 回退时必须返回稳定引用，否则每次通知都会触发重渲染 */
export const ASSIST_INIT: AssistSlice = {
  suggestions: [],
  expandChunks: {},
  intent: null,
  intentAction: null,
  emotion: null,
  emotionHistory: [],
  risk: null,
  dialogueTurns: [],
  nextActions: [],
  flowNext: [],
  steps: [],
  queryCards: [],
  sceneCards: [],
  commitment: null,
  scriptTips: [],
  expandAllSuggest: false,
  collapsedSections: { advice: false, script: false, kb: false },
  chatTrail: [],
  chatTrailFlow: '',
  clarifyResults: {},
  degrade: null,
};

export const QUALITY_INIT: QualitySlice = { alerts: [], folded: 0, hits: 0, deduct: 0 };

const SLICE_INIT: { [K in SliceName]: SliceMap[K] } = {
  assist: ASSIST_INIT,
  quality: QUALITY_INIT,
};

/** 挂断/关闭后桶的保留时长（供服务小结/回看对照） */
export const BUCKET_KEEP_MS = 10 * 60 * 1000;
/** 同时保留的桶上限（超出按 updatedAt 淘汰最旧） */
export const BUCKET_MAX = 8;

export function bucketKey(kind: CtxKind, ref: string | number): string {
  return `${kind}:${ref}`;
}

function emptyBucket(key: string): Bucket {
  const [kind, ...rest] = key.split(':');
  const now = Date.now();
  return {
    key,
    kind: kind as CtxKind,
    ref: rest.join(':'),
    createdAt: now,
    updatedAt: now,
    assist: ASSIST_INIT,
    quality: QUALITY_INIT,
  };
}

/**
 * 会话级快照：整页刷新会让本模块重建，桶若不落盘，坐席刚看到的内容就全没了——而切 tab、
 * 菜单往返却保留，口径不一致。**只落「刷新不该丢、且服务端重建不出来」的那一点**：
 * - 落：`chat:` 桶的指针 chatId + 该桶最新一条 intent / emotion（工具条胶囊）；
 * - 不落：语音桶（浏览器刷新会销毁 WebRTC，这一通已经断了，留内容只会显示不存在的「实时状态」；
 *   通话身份由 `/assist/active-call` 定位、说到哪句由 assist-forward-cursor 记）；
 * - 不落：质检告警（monitor 回填可重建）、对话缓冲（子页会话自带，重放会让它把问过的再问一遍）。
 * 读写与裁剪在 `context/snapshot.ts`（那侧不认识 Bucket，避免与本站成环）。
 */
/** 落盘节流：事件流逐条写桶，每个事件都序列化是白耗主线程 */
const PERSIST_THROTTLE_MS = 500;
/** 快照桶数上限（比 BUCKET_MAX 保守，防大卡片撑爆 sessionStorage 配额） */
const PERSIST_MAX_BUCKETS = 3;

/** 读会话级快照 → 内存桶（把落盘形态还原成完整 Bucket；过期/语音桶/非本人快照一律丢弃） */
function loadPersisted(): { chatId: number | null; buckets: Record<string, Bucket> } {
  const snap = readSnapshot(BUCKET_KEEP_MS);
  const buckets: Record<string, Bucket> = {};
  for (const sb of snap.buckets) {
    if (sb.key !== `chat:${sb.ref}`) continue;
    buckets[sb.key] = {
      key: sb.key,
      kind: 'chat',
      ref: sb.ref,
      createdAt: Number(sb.createdAt) || 0,
      updatedAt: Number(sb.updatedAt) || 0,
      assist: {
        ...ASSIST_INIT,
        // 只认对象形态：快照里的这两条是透传字段，被外部改坏时宁可空着，也不要让胶囊渲染出 undefined
        intent: sb.intent && typeof sb.intent === 'object' ? (sb.intent as IntentInfo) : null,
        emotion: sb.emotion && typeof sb.emotion === 'object' ? (sb.emotion as EmotionInfo) : null,
      },
      quality: QUALITY_INIT,
    };
  }
  return { chatId: snap.chatId, buckets };
}

/** 内存桶 → 落盘形态（裁剪在这里做：只留在线会话桶、只留两条胶囊值、超量按更新时间淘汰） */
function savePersisted(state: CtxStore) {
  const now = Date.now();
  const buckets = Object.values(state.buckets)
    .filter((b) => b.kind === 'chat' && now - b.updatedAt <= BUCKET_KEEP_MS)
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, PERSIST_MAX_BUCKETS)
    .map((b) => ({
      key: b.key,
      kind: b.kind,
      ref: b.ref,
      createdAt: b.createdAt,
      updatedAt: b.updatedAt,
      intent: b.assist.intent,
      emotion: b.assist.emotion,
    }));
  writeSnapshot({ chatId: state.chatId, buckets });
}

/** 丢弃会话级快照（换账号 / 「清理工作台」时调用；内存桶由调用方一起复原点） */
export function clearCtxSnapshot() {
  clearSnapshot();
}

interface CtxStore {
  /** 当前通话 uuid（语音语境唯一真源） */
  callUUID: string;
  /** 当前选中的在线会话 id（会话语境唯一真源） */
  chatId: number | null;
  /** 右栏语境：call=通话 / chat=会话（由 ChatPanel 上报） */
  view: 'call' | 'chat';
  buckets: Record<string, Bucket>;
  setCallUUID: (uuid: string) => void;
  setChatId: (id: number | null) => void;
  setView: (v: 'call' | 'chat') => void;
  /** 写入切片字段（值或更新函数；桶不存在则按 key 建桶） */
  write: (key: string, section: SliceName, field: string, value: unknown) => void;
  /** 重置切片到初值（切换语境/接听新通话时调用） */
  resetSlice: (key: string, section: SliceName) => void;
  /** 标记语境结束（挂断/关闭），保留供回看 */
  markEnded: (key: string) => void;
  /** 释放超时/超量桶 */
  prune: () => void;
}

/** 应用启动时的水合源：刷新后按 ref 直接读回上次的语境内容（通话 uuid 由服务端真源定位） */
const hydrated = loadPersisted();

export const useCtxStore = create<CtxStore>((set, getState) => ({
  callUUID: '',
  chatId: hydrated.chatId,
  view: 'call',
  buckets: hydrated.buckets,

  setCallUUID: (uuid) => {
    const prev = getState().callUUID;
    if (prev === uuid) return;
    set((s) => {
      const buckets = { ...s.buckets };
      // 上一通电话仍在桶里（可能已挂断）：确保新桶存在，旧桶交给 prune 回收
      if (uuid) {
        const key = bucketKey('call', uuid);
        if (!buckets[key]) buckets[key] = emptyBucket(key);
      }
      return { callUUID: uuid, buckets };
    });
    getState().prune();
  },

  setChatId: (id) => {
    if (getState().chatId === id) return;
    set((s) => {
      const buckets = { ...s.buckets };
      if (id) {
        const key = bucketKey('chat', id);
        if (!buckets[key]) buckets[key] = emptyBucket(key);
      }
      return { chatId: id, buckets };
    });
    getState().prune();
  },

  setView: (view) => {
    if (getState().view !== view) set({ view });
  },

  write: (key, section, field, value) =>
    set((s) => {
      const cur = s.buckets[key] ?? emptyBucket(key);
      const slice = { ...(cur[section] as unknown as Record<string, unknown>) };
      const prev = slice[field];
      slice[field] = typeof value === 'function' ? (value as (p: unknown) => unknown)(prev) : value;
      return {
        buckets: { ...s.buckets, [key]: { ...cur, [section]: slice as never, updatedAt: Date.now() } },
      };
    }),

  resetSlice: (key, section) =>
    set((s) => {
      const cur = s.buckets[key];
      if (!cur || cur[section] === SLICE_INIT[section]) return s;
      return { buckets: { ...s.buckets, [key]: { ...cur, [section]: SLICE_INIT[section] as never, updatedAt: Date.now() } } };
    }),

  markEnded: (key) =>
    set((s) => {
      const cur = s.buckets[key];
      if (!cur || cur.ended) return s;
      const now = Date.now();
      return { buckets: { ...s.buckets, [key]: { ...cur, ended: true, endedAt: now, updatedAt: now } } };
    }),

  prune: () =>
    set((s) => {
      const now = Date.now();
      let buckets = s.buckets;
      let changed = false;
      for (const [key, b] of Object.entries(buckets)) {
        if (b.ended && b.endedAt && now - b.endedAt > BUCKET_KEEP_MS) {
          if (!changed) {
            buckets = { ...buckets };
            changed = true;
          }
          delete buckets[key];
        }
      }
      const keys = Object.keys(buckets);
      if (keys.length > BUCKET_MAX) {
        if (!changed) buckets = { ...buckets };
        changed = true;
        keys
          .sort((a, b) => buckets[a].updatedAt - buckets[b].updatedAt)
          .slice(0, keys.length - BUCKET_MAX)
          .forEach((k) => delete buckets[k]);
      }
      return changed ? { buckets } : s;
    }),
}));

/** 已落盘的桶引用：用于跳过「状态对象换新但内容未变」的空写 */
let savedBuckets = hydrated.buckets;
let savedChatId = hydrated.chatId;
let persistTimer: number | null = null;

function flushPersist() {
  if (persistTimer !== null) {
    window.clearTimeout(persistTimer);
    persistTimer = null;
  }
  const s = useCtxStore.getState();
  savePersisted(s);
  savedBuckets = s.buckets;
  savedChatId = s.chatId;
}

function schedulePersist() {
  if (persistTimer !== null) return;
  persistTimer = window.setTimeout(() => {
    persistTimer = null;
    const s = useCtxStore.getState();
    if (s.buckets === savedBuckets && s.chatId === savedChatId) return;
    savePersisted(s);
    savedBuckets = s.buckets;
    savedChatId = s.chatId;
  }, PERSIST_THROTTLE_MS);
}

useCtxStore.subscribe(schedulePersist);
// 卸载前同步落盘一次：节流窗口内刚到的事件不因刷新丢失（pagehide 在刷新/跳转时都会触发）
window.addEventListener('pagehide', flushPersist);

// 换账号登录（登出后再登录 / 被顶下线）：内存桶与快照一并丢弃，
// 共享终端上不得把上一个坐席的会话与辅助内容留给下一个人。
useAuthStore.subscribe((s, prev) => {
  if (prev.user?.id === s.user?.id) return;
  clearCtxSnapshot();
  useCtxStore.setState({ chatId: null, buckets: {} });
});

/** 语境 key 解析：call 用显式 uuid（空串=不落桶），省略时取 store 当前 uuid；chat 用传入会话 id */
export function resolveCtxKey(spec: CtxSpec): string {
  if (spec.kind === 'chat') return spec.id ? bucketKey('chat', spec.id) : '';
  const uuid = spec.id === undefined || spec.id === null ? useCtxStore.getState().callUUID : String(spec.id);
  return uuid ? bucketKey('call', uuid) : '';
}

/** 订阅语境 key（call 的显式 uuid 优先；省略 id 时随 store 当前通话变化重渲染） */
export function useCtxKey(spec: CtxSpec): string {
  const callUUID = useCtxStore((s) => s.callUUID);
  if (spec.kind === 'chat') return spec.id ? bucketKey('chat', spec.id) : '';
  const uuid = spec.id === undefined || spec.id === null ? callUUID : String(spec.id);
  return uuid ? bucketKey('call', uuid) : '';
}

/** 订阅切片字段（drop-in 替代 useState 的读取侧） */
export function useCtxField<S extends SliceName, K extends keyof SliceMap[S]>(
  spec: CtxSpec,
  section: S,
  field: K,
  initial: SliceMap[S][K],
): SliceMap[S][K] {
  const key = useCtxKey(spec);
  const select = (s: CtxStore) => {
    const b = s.buckets[key];
    if (!b) return initial;
    const v = (b[section] as SliceMap[S])[field];
    return v === undefined ? initial : v;
  };
  const value = useCtxStore(select);
  return value;
}

/**
 * 语境状态（useState 的语境版）：同一 ref 下跨视图切换/菜单往返保留。
 * 注意 initial 必须是模块级常量（稳定引用），否则 selector 每次返回新引用会引发多余重渲染。
 */
export function useCtxState<S extends SliceName, K extends keyof SliceMap[S]>(
  spec: CtxSpec,
  section: S,
  field: K,
  initial: SliceMap[S][K],
): [SliceMap[S][K], (v: SliceMap[S][K] | ((prev: SliceMap[S][K]) => SliceMap[S][K])) => void] {
  const value = useCtxField(spec, section, field, initial);
  const kind = spec.kind;
  const id = spec.id;
  const set = useCallback(
    (v: SliceMap[S][K] | ((prev: SliceMap[S][K]) => SliceMap[S][K])) => {
      // 调用时解析 key：事件回调里可能刚自愈出 uuid，闭包里的 key 会是旧的
      const key = resolveCtxKey({ kind, id });
      if (!key) return;
      useCtxStore.getState().write(key, section, field as string, v);
    },
    [kind, id, section, field],
  );
  return [value, set];
}

/** 语境动作（重置/结束/释放），供面板在接听、挂断、切会话时调用 */
export function useCtxActions() {
  const resetSliceRaw = useCtxStore((s) => s.resetSlice);
  const markEndedRaw = useCtxStore((s) => s.markEnded);
  const prune = useCtxStore((s) => s.prune);
  const resetSlice = useCallback((spec: CtxSpec, section: SliceName) => resetSliceRaw(resolveCtxKey(spec), section), [resetSliceRaw]);
  const markEnded = useCallback((spec: CtxSpec) => markEndedRaw(resolveCtxKey(spec)), [markEndedRaw]);
  const actions = { resetSlice, markEnded, prune };
  return actions;
}
